import { ElementRef, Injectable } from '@angular/core';
import {
    ILtChartData,
    IMciChartData,
    IMciData,
    LoadTruckData,
    MciData,
} from '../../models';
import * as am5 from '@amcharts/amcharts5';
import * as am5xy from '@amcharts/amcharts5/xy';
import am5themes_Animated from '@amcharts/amcharts5/themes/Animated';
import { noop } from 'lodash';

@Injectable({
    providedIn: 'root',
})
export class IqMcChartsUtilsService {
    constructor() {}
    // #region MCI Prior Graph
    async renderMciPriorGraph(root: am5.Root, chart: am5xy.XYChart, mciData: MciData
    ) {
        this._clearChart(chart);
        const chartData: IMciChartData[] = this.parseMciChartData(mciData);
        const gridIntervals: am5.time.ITimeInterval[] =
            chartData.length > 8
                ? [{ timeUnit: 'day', count: 4 }]
                : [{ timeUnit: 'day', count: 1 }];
        //xAxis
        let xAxis = this._renderXAxis(root, chart, gridIntervals);
        let xRenderer = xAxis.get('renderer');
        xRenderer.labels.template.setAll({
            fontSize: 11,
            fontWeight: '400',
            fill: am5.color('#636D79'),
            paddingTop: 10,
            minPosition: 0.01,
            maxPosition: 0.99,
        });
        xRenderer.grid.template.setAll({
            visible: false,
        });

        //yAxis
        let yAxis = this._renderMciYAxis(root, chart);
        let yRenderer = yAxis.get('renderer');
        yRenderer.labels.template.setAll({
            forceHidden: true,
            fontWeight: '400',
            fill: am5.color('#636D79'),
            fontSize: 11,
        });
        yRenderer.grid.template.setAll({
            fill: am5.color('#E9ECF1'),
            strokeWidth: 1,
            strokeDasharray: [3, 4],
        });

        //Min/max & zero labels on y-axis

        // +100 label
        this.addMinMaxLabel(yAxis, 100, '+100');
        // -100 label
        this.addMinMaxLabel(yAxis, -100, '-100');
        // 0 label
        this.addMinMaxLabel(yAxis, 0, '0');

        //Neutral Range
        let neutralDataItem = yAxis.makeDataItem({
            value: -50,
            endValue: 50,
        });

        let neutralRange = yAxis.createAxisRange(neutralDataItem);
        neutralRange.get('axisFill').setAll({
            fill: am5.color('#FFFDE5'),
            fillOpacity: 1,
            visible: true,
        });

        //Tight
        this.addMarketCapacityLabel(chart, yAxis, 100, 'Tight');
        //Neutral
        this.addMarketCapacityLabel(chart, yAxis, 0, 'Neutral');
        //Loose
        this.addMarketCapacityLabel(chart, yAxis, -100, 'Loose');
        //MCI Score Label
        this.addAxisTitleLabel(yAxis, 50, 'MCI Score');

        //tooltip
        let tooltip = am5.Tooltip.new(root, {
            getFillFromSprite: false,
            pointerOrientation: 'vertical',
            html: `<div class="text-center p-0 m-0 flex flex-col text-[10px]" >
                <p class="p-0 m-0 pb-[2px]">{valueX.formatDate("MMMM dd, YYYY")}</p>
                <p class="p-0 m-0 pb-[2px]">{capacityLevel}</p>
                <div class="flex flex-row justify-between gap-x-[5px]">
                    <span class="">MCI:</span> <span class="font-bold">{valueY}</span>
                </div>
             </div>`,
        });
        tooltip.get('background').setAll({
            fill: am5.color('#ffffff'),
            fillOpacity: 1,
            stroke: am5.color('#3600A3'),
            strokeOpacity: 1,
        });
        tooltip.events.on(
            'globalpointermove',
            (evt: am5.ISpritePointerEvent) => {
                const yPos = evt?.target?.y() ?? 0;
                const height = evt?.target?.height();

                tooltip.set('pointerOrientation', 'down');
                tooltip.set('dy', -yPos - height / 2 + 15);
            },
        );
        //Line Series
        let series = this._generateSeries(
            'MCI Prior',
            root,
            chart,
            xAxis,
            yAxis,
            'date',
            'mci',
            tooltip,
        );

        this._generateCursor(root, chart, xAxis);
        series.data.setAll(chartData);
        series.appear(1000);
        chart.appear(1000, 100);
    }

    parseMciChartData(mciData: MciData): IMciChartData[] {
        const data = mciData.map((data) => {
            return {
                date: new Date(data.date).getTime(),
                mci: data.mci,
                capacityLevel: data.capacity
            } as IMciChartData;
        });
        return data;
    }

    parseMciForecastChartData(mciData: MciData): IMciChartData[] {
        const data = mciData.map((data) => {
            return {
                date: new Date(data.date).getTime(),
                mci: data.mci,
                capacityLevel: data.capacity,
                upperInterval: data.predictedIntervalUpperBound,
                lowerInterval: data.predictedIntervalLowerBound,
            } as IMciChartData;
        });
        return data;
    }

    addMarketCapacityLabel(
        chart: am5xy.XYChart,
        axis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        value: number,
        text: string,
    ) {
        let dataItem = axis.makeDataItem({
            value: value,
        });
        let range = axis.createAxisRange(dataItem);
        range.get('label').setAll({
            text: text,
            fontSize: 11,
            fill: am5.color('#636D79'),
            fontWeight: '400',
            forceHidden: false,
            inside: true,
            centerX: 0,
            dx: 10,
        });
        range.get('label').adapters.add('x', (x, target) => {
            return chart.plotContainer.width();
        });
        chart.plotContainer.onPrivate('width', () => {
            range.get('label').markDirtyPosition();
        });
    }

    addMinMaxLabel(
        axis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        value: number,
        text: string,
    ) {
        let dataItem = axis.makeDataItem({
            value: value,
        });
        let range = axis.createAxisRange(dataItem);
        range.get('label').setAll({
            text: text,
            forceHidden: false,
            paddingRight: 10,
        });
    }

    addAxisTitleLabel(
        axis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        value: number,
        text: string,
    ) {
        let dataItem = axis.makeDataItem({
            value: value,
        });
        let range = axis.createAxisRange(dataItem);
        range.get('label').setAll({
            text: text,
            forceHidden: false,
            fill: am5.color('#192129'),
            fontWeight: '500',
            rotation: 270,
            fontSize: 12,
            paddingRight: 15,
            centerY: 60,
        });
    }
    // #endregion MCI Prior Graph

    // #region Load Truck Graph

    async renderLtGraph(
        root: am5.Root,
        chart: am5xy.XYChart,
        ltData: LoadTruckData,
    ) {
        this._clearChart(chart);
        const chartData: ILtChartData[] = this.parseLtChartData(ltData);
        const gridIntervals: am5.time.ITimeInterval[] =
            chartData.length > 8
                ? [{ timeUnit: 'day', count: 4 }]
                : [{ timeUnit: 'day', count: 1 }];
        let maxRatio = Math.max(...ltData.map((data) => data.ratio)) + 1;

        //xAxis
        let xAxis = this._renderXAxis(root, chart, gridIntervals);
        let xRenderer = xAxis.get('renderer');
        xRenderer.labels.template.setAll({
            fontSize: 11,
            fontWeight: '400',
            fill: am5.color('#636D79'),
            paddingTop: 10,
            minPosition: 0.01,
            maxPosition: 0.99,
        });
        xRenderer.grid.template.setAll({
            visible: false,
        });

        //yAxis
        let yAxis = chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                min: 0,
                max: Math.ceil(maxRatio),
                renderer: am5xy.AxisRendererY.new(root, {}),
            }),
        );
        let yRenderer = yAxis.get('renderer');
        yRenderer.labels.template.setAll({
            fontWeight: '400',
            fill: am5.color('#636D79'),
            fontSize: 11,
            paddingLeft: 40,
        });
        yRenderer.grid.template.setAll({
            fill: am5.color('#E9ECF1'),
            strokeWidth: 1,
            strokeDasharray: [3, 4],
        });

        //tooltip
        let tooltip = am5.Tooltip.new(root, {
            getFillFromSprite: false,
            getStrokeFromSprite: false,
            pointerOrientation: 'vertical',
            html: ` <div class="text-xs flex flex-col gap-y-[4px] text-dat-grey-900">
                        <div class="flex justify-between gap-x-4">
                            <span class="">L/T Ratio:</span>
                            <span class="">{ratio}</span>
                        </div>
                        <div class="relative h-2 !bg-dat-grey-100">
                            <div class="absolute h-full !bg-dat-grey-300" style="width: {gaugeWidth}%;"></div>
                        </div>
                        <div class="flex justify-between">
                            <span class="">Loads:</span>
                            <span class="">{loads}</span>
                        </div>
                        <div class="flex justify-between">
                            <span class="">Trucks:</span>
                            <span class="">{trucks}</span>
                        </div>
                    </div>`,
        });
        tooltip.get('background').setAll({
            fill: am5.color('#ffffff'),
            fillOpacity: 1,
            stroke: am5.color('#E9ECF1'),
            strokeOpacity: 1,
        });
        tooltip.events.on(
            'globalpointermove',
            (evt: am5.ISpritePointerEvent) => {
                const yPos = evt?.target?.y() ?? 0;
                const height = evt?.target?.height();

                tooltip.set('pointerOrientation', 'down');
                tooltip.set('dy', -yPos - height / 2 + 45);
            },
        );
        //Set L/T Ratio Label
        chart.children.unshift(
            am5.Label.new(root, {
                text: 'L/T Ratio',
                fontSize: 12,
                fontWeight: '500',
                fill: am5.color('#192129'),
                y: am5.percent(60),
                dx: 0,
                paddingRight: 15,
                rotation: 270,
                forceHidden: false,
            }),
        );
        //Line Series
        let series = this._generateSeries(
            'L/T Ratio',
            root,
            chart,
            xAxis,
            yAxis,
            'date',
            'ratio',
            tooltip,
        );

        this._generateCursor(root, chart, xAxis);
        series.data.setAll(chartData);
        series.appear(1000);
        chart.appear(1000, 100);
    }

    parseLtChartData(ltData: LoadTruckData): ILtChartData[] {
        const data = ltData.map((data) => {
            return {
                date: new Date(data.date).getTime(),
                loads: data.loads,
                trucks: data.trucks,
                ratio: Math.round(data.ratio * 100) / 100,
                gaugeWidth: (data.loads / (data.loads + data.trucks)) * 100,
            } as ILtChartData;
        });
        return data;
    }
    // #endregion Load Truck Graph

    // #region MCI Forecast Graph
    async renderMciForecastGraph(root: am5.Root,chart: am5xy.XYChart, mciData: MciData) {
        this._clearChart(chart);
        const chartData: IMciChartData[] =
            this.parseMciForecastChartData(mciData);
        const gridIntervals: am5.time.ITimeInterval[] =
            chartData.length > 8
                ? [{ timeUnit: 'day', count: 4 }]
                : [{ timeUnit: 'day', count: 1 }];
        //xAxis
        let xAxis = this._renderXAxis(root, chart, gridIntervals);
        let xRenderer = xAxis.get('renderer');
        xRenderer.labels.template.setAll({
            fontSize: 11,
            fontWeight: '400',
            fill: am5.color('#636D79'),
            paddingTop: 10,
            minPosition: 0.01,
            maxPosition: 0.99,
        });
        xRenderer.grid.template.setAll({
            visible: false,
        });

        //yAxis
        let yAxis = this._renderMciYAxis(root, chart);
        let yRenderer = yAxis.get('renderer');
        yRenderer.labels.template.setAll({
            forceHidden: true,
            fontWeight: '400',
            fill: am5.color('#636D79'),
            fontSize: 11,
        });
        yRenderer.grid.template.setAll({
            fill: am5.color('#E9ECF1'),
            strokeWidth: 1,
            strokeDasharray: [3, 4],
        });

        //Min/max & zero labels on y-axis

        // +100 label
        this.addMinMaxLabel(yAxis, 100, '+100');
        // -100 label
        this.addMinMaxLabel(yAxis, -100, '-100');
        // 0 label
        this.addMinMaxLabel(yAxis, 0, '0');

        //Neutral Range
        let neutralDataItem = yAxis.makeDataItem({
            value: -50,
            endValue: 50,
        });

        let neutralRange = yAxis.createAxisRange(neutralDataItem);

        neutralRange.get('axisFill').setAll({
            fill: am5.color('#FFFDE5'),
            fillOpacity: 1,
            visible: true,
        });

        //Tight
        this.addMarketCapacityLabel(chart, yAxis, 100, 'Tight');
        //Neutral
        this.addMarketCapacityLabel(chart, yAxis, 0, 'Neutral');
        //Loose
        this.addMarketCapacityLabel(chart, yAxis, -100, 'Loose');

        //MCI Score Label
        this.addAxisTitleLabel(yAxis, 50, 'MCI Score');

        //tooltip
        let tooltip = am5.Tooltip.new(root, {
            getFillFromSprite: false,
            pointerOrientation: 'vertical',
            html: `<div class="text-center text-[10px] p-0 m-0 flex flex-col" >
                <p class="pb-[1px]">{valueX.formatDate("MMMM dd, YYYY")}</p>
                <p class="p-0 m-0 pb-[2px]">{capacityLevel}</p>
                <div class="flex flex-row justify-between gap-x-[5px]">
                    <span class="">Forecast MCI:</span> <span class="font-bold">{valueY}</span>
                </div>
               <div class="flex flex-row justify-between gap-x-[5px]">
                    <span class="">Lower Interval:</span> <span class="font-bold">{lowerInterval}</span>
                </div>
                <div class="flex flex-row justify-between gap-x-[5px]">
                    <span class="">Upper Interval:</span> <span class="font-bold">{upperInterval}</span>
                </div>
                </div>`,
            paddingTop: 0,
            marginTop: 0,
        });
        tooltip.get('background').setAll({
            fill: am5.color('#ffffff'),
            fillOpacity: 1,
            stroke: am5.color('#3600A3'),
            strokeOpacity: 1,
        });
        tooltip.events.on(
            'globalpointermove',
            (evt: am5.ISpritePointerEvent) => {
                const yPos = evt?.target?.y() ?? 0;
                const height = evt?.target?.height();

                tooltip.set('pointerOrientation', 'down');
                tooltip.set('dy', -yPos - height / 2 + 15);
            },
        );

        // Line Series
        let series = this._generateSeries(
            'MCI Forecast',
            root,
            chart,
            xAxis,
            yAxis,
            'date',
            'mci',
            tooltip,
            true,
        );

        let range = chart.series.push(
            am5xy.LineSeries.new(root, {
                name: 'MCI Forecast Range',
                xAxis: xAxis,
                yAxis: yAxis,
                valueXField: 'date',
                valueYField: 'lowerInterval',
                openValueYField: 'upperInterval',
                stroke: am5.color('#3600A3'),
            }),
        );

        range.fills.template.setAll({
            fill: am5.color('#3600A3'),
            fillOpacity: 0.1,
            visible: true,
        });

        range.strokes.template.setAll({
            opacity: 0,
        });

        this._generateCursor(root, chart, xAxis);
        range.data.setAll(chartData);
        range.appear(1000);
        series.data.setAll(chartData);
        series.appear(1000);
        chart.appear(1000, 100);
    }

    // #endregion MCI Forecast Graph

    renderGraph(container: ElementRef<any>): {
        root: am5.Root;
        chart: am5xy.XYChart;
    } {
        const root = am5.Root.new(container.nativeElement, {
            tooltipContainerBounds: {
                top: 150,
                bottom: 100,
                left: 100,
                right: 100,
            },
        });
        root.setThemes([am5themes_Animated.new(root)]);

        let chart = root.container.children.push(
            am5xy.XYChart.new(root, {
                paddingRight: 60,
            }),
        );

        return { root, chart };
    }

    private _renderXAxis(
        root: am5.Root,
        chart: am5xy.XYChart,
        gridIntervals: am5.time.ITimeInterval[],
    ): am5xy.DateAxis<am5xy.AxisRenderer> {
        const xAxis = chart.xAxes.push(
            am5xy.DateAxis.new(root, {
                baseInterval: { timeUnit: 'day', count: 1 },
                renderer: am5xy.AxisRendererX.new(root, {}),
                markUnitChange: false,
                tooltip: am5.Tooltip.new(root, {}),
                gridIntervals,
            }),
        );

        xAxis.set(
            'tooltip',
            am5.Tooltip.new(root, {
                forceHidden: true,
            }),
        );

        xAxis.get('dateFormats')['day'] = 'EEE MM/dd';

        return xAxis;
    }

    private _renderMciYAxis(
        root: am5.Root,
        chart: am5xy.XYChart,
    ): am5xy.ValueAxis<am5xy.AxisRenderer> {
        return chart.yAxes.push(
            am5xy.ValueAxis.new(root, {
                min: -100,
                max: 100,
                renderer: am5xy.AxisRendererY.new(root, {}),
            }),
        );
    }

    private _generateSeries(
        name: string,
        root: am5.Root,
        chart: am5xy.XYChart,
        xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
        yAxis: am5xy.ValueAxis<am5xy.AxisRenderer>,
        xAxisValue: string,
        yAxisValue: string,
        tooltip: am5.Tooltip | null,
        dashed: boolean = false,
    ): am5xy.LineSeries {
        let series = chart.series.push(
            am5xy.LineSeries.new(root, {
                name,
                xAxis: xAxis,
                yAxis: yAxis,
                valueXField: xAxisValue,
                valueYField: yAxisValue,
                tooltip: tooltip,
                stroke: am5.color('#3600A3'),
            }),
        );

        series.strokes.template.setAll({
            strokeWidth: 2,
        });

        !!dashed
            ? series.strokes.template.setAll({
                  strokeDasharray: [3, 4],
              })
            : noop();

        series.bullets.push(() => {
            return am5.Bullet.new(root, {
                sprite: am5.Rectangle.new(root, {
                    width: 7,
                    height: 7,
                    fill: am5.color('#3600A3'),
                    centerX: 4,
                    centerY: 4,
                    tooltip: am5.Tooltip.new(root, {}),
                }),
            });
        });
        return series;
    }

    private _generateCursor(
        root: am5.Root,
        chart: am5xy.XYChart,
        xAxis: am5xy.DateAxis<am5xy.AxisRenderer>,
    ): am5xy.XYCursor {
        let cursor = chart.set(
            'cursor',
            am5xy.XYCursor.new(root, {
                xAxis,
            }),
        );
        cursor.lineY.set('visible', false);
        return cursor;
    }

    private _clearChart(chart: am5xy.XYChart) {
        chart.series.clear();
        chart.xAxes.clear();
        chart.yAxes.clear();
        //Adding this the dispose calls here because for some reason the heap size keeps increasing even after series are cleared
        chart.series.dispose();
        chart.xAxes.dispose();
        chart.yAxes.dispose();
    }
}
